import React, { useState, forwardRef } from "react";
import {
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
} from "@mui/icons-material";
import { sanitizeInput } from "@/shared/lib";

export interface FormFieldProps {
  name: string;
  label: string;
  type?: "text" | "email" | "password" | "tel";
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  autoComplete?: string;
  startIcon?: React.ReactNode;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      name,
      label,
      type = "text",
      value,
      onChange,
      error,
      required = false,
      placeholder,
      disabled = false,
      autoComplete,
      startIcon,
      onKeyDown,
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const sanitizedValue = sanitizeInput(event.target.value);
      onChange(sanitizedValue);
    };

    const getStartIcon = () => {
      if (startIcon) return startIcon;

      switch (type) {
        case "email":
          return <Email color="action" />;
        case "password":
          return <Lock color="action" />;
        default:
          return <Person color="action" />;
      }
    };

    const getInputType = () => {
      if (type === "password") {
        return showPassword ? "text" : "password";
      }
      return type;
    };

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    const getEndAdornment = () => {
      if (type === "password") {
        return (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={togglePasswordVisibility}
              edge="end"
              disabled={disabled}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        );
      }
      return null;
    };

    return (
      <FormControl fullWidth variant="outlined" error={!!error}>
        <InputLabel htmlFor={name} required={required}>
          {label}
        </InputLabel>
        <OutlinedInput
          ref={ref}
          id={name}
          name={name}
          type={getInputType()}
          value={value}
          onChange={handleChange}
          {...(onKeyDown ? { onKeyDown } : {})}
          {...(disabled !== undefined ? { disabled } : {})}
          {...(placeholder !== undefined ? { placeholder } : {})}
          {...(autoComplete !== undefined ? { autoComplete } : {})}
          startAdornment={
            <InputAdornment position="start">{getStartIcon()}</InputAdornment>
          }
          endAdornment={getEndAdornment()}
          label={label}
          required={required}
          inputProps={{
            "aria-describedby": error ? `${name}-error` : undefined,
          }}
        />
        {error && (
          <FormHelperText id={`${name}-error`}>
            {error}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
);

FormField.displayName = "FormField";

export default FormField;
